import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, dayjsLocalizer } from 'react-big-calendar';
import dayjs from 'dayjs';
import 'dayjs/locale/es-do';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Box, useMediaQuery, styled, useTheme, Avatar } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Typography from '@mui/material/Typography';
import EventNoteIcon from '@mui/icons-material/EventNote';
import HttpsIcon from '@mui/icons-material/Https';

import LoadingInformation from '../../Components/LoadingInformation';

dayjs.locale('es-do');
const localizer = dayjsLocalizer(dayjs);

const StyledContainer = styled('div')(({ theme, isMobile, isTablet, isSmallScreen }) => ({
	width: isSmallScreen ? '100%' : isMobile ? '95%' : isTablet ? '92%' : '90%',
	margin: isMobile ? '0.5rem auto' : '2rem auto',
	backgroundColor: theme.palette.background.paper,
	borderRadius: isMobile ? '8px' : '16px',
	boxShadow: theme.shadows[3],
	padding: isSmallScreen ? theme.spacing(1) : isMobile ? theme.spacing(1.5) : isTablet ? theme.spacing(2.5) : theme.spacing(3),
	transition: 'all 0.3s ease',
	minHeight: isMobile ? '95vh' : '90vh',
	overflow: 'auto',
	[theme.breakpoints.down('sm')]: {
		margin: '0',
		borderRadius: '4px',
		width: '95%',
	}
}));

const EventWrapper = styled('div')(({ theme, type, isMobile, isSmallScreen }) => ({
	color: theme.palette.common.white,
	borderRadius: isMobile ? '6px' : '8px',
	padding: theme.spacing(isSmallScreen ? 0.5 : isMobile ? 0.75 : 1),
	margin: isSmallScreen ? '1px' : '2px',
	fontSize: isSmallScreen ? '0.7rem' : isMobile ? '0.75rem' : '0.875rem',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	transition: 'transform 0.2s, box-shadow 0.2s',
	cursor: 'pointer',
	display: 'flex',
	flexDirection: isSmallScreen ? 'column' : 'row',
	alignItems: 'center',
	gap: isSmallScreen ? '2px' : isMobile ? '4px' : '8px',
	minHeight: isSmallScreen ? '32px' : isMobile ? '36px' : 'auto',
	'&:hover': {
		transform: isMobile ? 'scale(1.02)' : 'translateY(-2px)',
		boxShadow: theme.shadows[4],
	},
}));

const AgendaEventWrapper = styled('div')(({ theme, isMobile, isSmallScreen }) => ({
	padding: theme.spacing(isSmallScreen ? 1 : isMobile ? 1.5 : 2),
	backgroundColor: theme.palette.grey[100],
	borderRadius: isMobile ? '6px' : '8px',
	margin: theme.spacing(isSmallScreen ? 0.5 : 1, 0),
	display: 'flex',
	flexDirection: isSmallScreen ? 'column' : 'row',
	alignItems: isSmallScreen ? 'flex-start' : 'center',
	gap: isSmallScreen ? '8px' : '16px',
	transition: 'background-color 0.2s',
	'&:hover': {
		backgroundColor: theme.palette.common.white,
	},
}));

const EventIconWrapper = styled('div')(({ theme }) => ({
	backgroundColor: theme.palette.common.white,
	borderRadius: '50%',
	padding: theme.spacing(1),
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	boxShadow: theme.shadows[1],
}));

const HeaderContainer = styled('div')(({ theme, isMobile, isSmallScreen }) => ({
	display: 'flex',
	flexDirection: 'row',
	alignItems: isSmallScreen ? 'flex-start' : 'center',
	gap: theme.spacing(isMobile ? 1 : 2),
	marginBottom: theme.spacing(isMobile ? 3 : 4),
	padding: theme.spacing(isSmallScreen ? 1.5 : isMobile ? 2 : 2),
	backgroundColor: '#f759ab',
	borderRadius: isMobile ? '6px' : '8px',
	color: theme.palette.primary.contrastText,
	[theme.breakpoints.down('sm')]: {
		textAlign: 'center',
		justifyContent: 'center',
	}
}));

const messages = {
	next: "Siguiente",
	previous: "Anterior",
	today: "Hoy",
	month: "Mes",
	week: "Semana",
	day: "Día",
	date: "Fecha",
	time: "Hora",
	event: "Descripción del evento",
	allDay: "Todo el día",
};

const CalendarStyles = styled('div')({
	'.rbc-date-cell': {
		position: 'relative',
	},
	'.has-events': {
		position: 'relative',
	},
	'.rbc-month-row, .rbc-month-view, .rbc-row-content': {
		overflow: 'visible !important', // Permite que los eventos sean visibles
	},
	'.rbc-day-bg': {
		position: 'relative',
	},
});

function EventsCalendar() {
	const [events, setEvents] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [authenticationError, setAuthenticationError] = useState(false);

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const isTablet = useMediaQuery(theme.breakpoints.down('md'));
	const isSmallScreen = useMediaQuery('(max-width:480px)');
	const navigate = useNavigate();

	const backgroundColor = (event) => {
		if (!event || !event.title) {
			return '#9e9e9e'; // Color por defecto
		}
		
		const title = event.title.toLowerCase();
		
		if (title.includes('boda')) {
			return '#9c27b0';
		} else if (title.includes('quinceaños')) {
			return '#e91e63';
		} else if (title.includes('cumpleaños')) {
			return '#1976d2';
		} else {
			// Color para eventos genéricos
			return '#9e9e9e';
		}
	}

	const eventTypeIconHandler = (event) => {
		switch (event.type) {
			case 'outside':
				return <AirportShuttleIcon fontSize={isSmallScreen ? "small" : "medium"} sx={{ color: '#ff9800' }} />;
			default:
				return <BusinessIcon fontSize={isSmallScreen ? "small" : "medium"} sx={{ color: '#009688' }} />;
		}
	};

	const eventStyleGetter = (event) => ({
		style: {
			backgroundColor: backgroundColor(event),
			borderRadius: isMobile ? '4px' : '8px',
			color: 'white',
			border: 'none',
			fontSize: isSmallScreen ? '0.65rem' : isMobile ? '0.7rem' : isTablet ? '0.8rem' : '0.875rem',
			padding: isSmallScreen ? '2px' : isMobile ? '3px' : '4px',
			display: 'flex',
			alignItems: 'center',
			gap: isSmallScreen ? '2px' : '4px',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: isSmallScreen ? 'nowrap' : 'normal',
			minHeight: isSmallScreen ? '20px' : isMobile ? '24px' : 'auto',
		},
	});

	const listEvents = async () => {
		const token = localStorage.getItem('token');

		try {
			const response = await fetch('https://dprincess-training.servehttp.com/events', {
				method: 'GET',
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			if (!response.ok) throw new Error("Error al cargar eventos");

			const data = await response.json();
			
			// Validar y filtrar eventos antes de formatear
			const validEvents = data.filter(event => 
				event && 
				event.eventTitle && 
				event.eventDateStart && 
				event.eventDateEnd &&
				event.eventID
			);

			const formattedEvents = validEvents.map((event) => ({
				start: dayjs(event.eventDateStart).toDate(),
				end: dayjs(event.eventDateEnd).toDate(),
				title: event.eventTitle || 'Sin título',
				description: event.eventDescription || 'Sin descripción',
				type: event.eventType || 'default',
				status: event.status || 'active',
				eventID: event.eventID,
			})).filter(event => 
				// Doble validación: asegurar que los eventos formateados sean válidos
				event.title && 
				event.start && 
				event.end && 
				event.eventID &&
				!isNaN(event.start.getTime()) && 
				!isNaN(event.end.getTime())
			);

			setEvents(formattedEvents);
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		listEvents();
	}, []);

	const EventComponent = ({ event }) => {
		// Validación del evento
		if (!event || !event.title || !event.eventID) {
			return null;
		}

		return (
			<EventWrapper type={event.type} isMobile={isMobile} isSmallScreen={isSmallScreen} >
				<EventIconWrapper
					onClick={() => navigate(`/events/view-event-details/${event.eventID}`)}
					sx={{
						minWidth: isSmallScreen ? '16px' : isMobile ? '20px' : 'auto',
						minHeight: isSmallScreen ? '16px' : isMobile ? '20px' : 'auto',
						padding: isSmallScreen ? '2px' : isMobile ? '4px' : '8px',
					}}
				>
					{eventTypeIconHandler(event)}
				</EventIconWrapper>
				<div style={{
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					minWidth: 0,
					flex: 1
				}}>
					<strong style={{
						fontSize: isSmallScreen ? '0.65rem' : isMobile ? '0.7rem' : '0.875rem',
						lineHeight: 1.2
					}}>
						{isSmallScreen ? event.title.substring(0, 15) + (event.title.length > 15 ? '...' : '') : event.title}
					</strong>
					{!isMobile && (
						<div style={{ fontSize: '0.75rem', opacity: 0.9 }}>
							{event.description || 'Sin descripción'}
						</div>
					)}
				</div>
			</EventWrapper>
		);
	};

	const AgendaEvent = ({ event }) => {
		// Validación del evento
		if (!event || !event.title || !event.start || !event.end) {
			return null;
		}

		return (
			<AgendaEventWrapper isMobile={isMobile} isSmallScreen={isSmallScreen}>
				<EventIconWrapper sx={{
					minWidth: isSmallScreen ? '32px' : '40px',
					minHeight: isSmallScreen ? '32px' : '40px',
					alignSelf: isSmallScreen ? 'center' : 'flex-start'
				}}>
					{eventTypeIconHandler(event)}
				</EventIconWrapper>
				<div style={{ flex: 1, minWidth: 0 }}>
					<strong style={{
						color: backgroundColor(event),
						fontSize: isSmallScreen ? '0.9rem' : isMobile ? '1rem' : '1.1rem',
						display: 'block',
						marginBottom: isSmallScreen ? '4px' : '8px'
					}}>
						{event.title}
					</strong>
					<p style={{
						margin: isSmallScreen ? '4px 0' : '8px 0',
						color: '#666',
						fontSize: isSmallScreen ? '0.8rem' : '0.9rem',
						lineHeight: 1.4
					}}>
						{isSmallScreen ?
							((event.description || 'Sin descripción').length > 60 ? 
								(event.description || 'Sin descripción').substring(0, 60) + '...' : 
								(event.description || 'Sin descripción')) :
							(event.description || 'Sin descripción')
						}
					</p>
					<small style={{
						color: '#999',
						fontSize: isSmallScreen ? '0.7rem' : '0.8rem'
					}}>
						{dayjs(event.start).format('HH:mm')} - {dayjs(event.end).format('HH:mm')}
					</small>
				</div>
			</AgendaEventWrapper>
		);
	};

	const dayPropGetter = (date) => {
		const dayEvents = events.filter(
			(event) =>
				event && 
				event.start && 
				dayjs(event.start).format('YYYY-MM-DD') === dayjs(date).format('YYYY-MM-DD')
		);

		if (dayEvents.length > 0) {
			return {
				className: 'has-events',
				style: {
					position: 'relative',
					backgroundColor: 'rgba(25, 118, 210, 0.1)',
				},
			};
		}

		return {};
	};

	// Filtrar eventos válidos antes de renderizar
	const validEvents = events.filter(event => 
		event && 
		event.title && 
		event.start && 
		event.end && 
		event.eventID
	);

	if (loading) {
		return <LoadingInformation />;
	};

	if (authenticationError) {
		return (
			<Container maxWidth="sm" sx={{ py: 8 }}>
				{contextHolder}
				<Box sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					minHeight: '60vh',
					gap: 2
				}}>
					<Avatar sx={{ bgcolor: '#f44336', width: 64, height: 64 }}>
						{/* Icono de candado para indicar acceso restringido */}
						<HttpsIcon sx={{ fontSize: 40 }} />
					</Avatar>
					<Typography variant="h5" sx={{ color: '#f44336', fontWeight: 700 }}>
						No tienes permiso para acceder a esta sección
					</Typography>
					<Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
						Esta información solo está disponible para usuarios registrados.
					</Typography>
				</Box>
			</Container>
		);
	};

	if (error) {
		return (
			<Box
				display="flex"
				flexDirection="column"
				alignItems="center"
				justifyContent="center"
				minHeight="80vh"
				color="error.main"
			>
				<ErrorOutlineIcon fontSize="large" />
				<Typography variant="h6" mt={2}>
					Error al cargar eventos
				</Typography>
				<Typography variant="body2" mt={1} color="text.secondary">
					{error}
				</Typography>
			</Box>
		);
	};

	return (
		<StyledContainer isMobile={isMobile} isTablet={isTablet} isSmallScreen={isSmallScreen}>
			<HeaderContainer isMobile={isMobile} isSmallScreen={isSmallScreen}>
				<EventNoteIcon fontSize={isSmallScreen ? "medium" : "large"} />
				<Typography
					variant={isSmallScreen ? "h6" : isMobile ? "h5" : "h4"}
					component="h1"
					sx={{
						fontSize: isSmallScreen ? '1.1rem' : isMobile ? '1.5rem' : '2rem',
						fontWeight: 600
					}}
				>
					Calendario de Eventos
					<Typography
						variant={isSmallScreen ? "caption" : "subtitle1"}
						component="span"
						sx={{
							ml: isSmallScreen ? 1 : 2,
							opacity: 0.9,
							display: isSmallScreen ? 'block' : 'inline',
							fontSize: isSmallScreen ? '0.75rem' : '1rem'
						}}
					>
						({events.length} eventos programados)
					</Typography>
				</Typography>
			</HeaderContainer>
			<CalendarStyles>
				<Calendar
					localizer={localizer}
					events={validEvents}
					views={isSmallScreen ? ['agenda', 'day'] : isMobile ? ['agenda', 'day', 'week'] : ['month', 'week', 'day', 'agenda']}
					defaultView={isSmallScreen ? 'agenda' : isMobile ? 'agenda' : 'month'}
					min={new Date(0, 0, 0, 8)}
					max={new Date(0, 0, 0, 23)}
					eventPropGetter={eventStyleGetter}
					dayPropGetter={dayPropGetter}
					messages={messages}
					components={{
						event: EventComponent,
						agenda: { event: AgendaEvent },
					}}
					style={{
						height: isSmallScreen ? 'calc(100vh - 140px)' : isMobile ? 'calc(100vh - 120px)' : isTablet ? '70vh' : '75vh',
						fontFamily: 'Roboto, sans-serif',
						fontSize: isSmallScreen ? '0.75rem' : isMobile ? '0.875rem' : '1rem',
					}}
					dayLayoutAlgorithm="no-overlap"
					popup={!isMobile}
					popupOffset={30}
					showMultiDayTimes={!isSmallScreen}
					step={isMobile ? 60 : 30}
					timeslots={isMobile ? 1 : 2}
					onSelectEvent={(event) => navigate(`/events/view-event-details/${event.eventID}`)}
					componentsProps={{
						toolbar: {
							style: {
								backgroundColor: '#f5f5f5',
								borderRadius: isMobile ? '4px' : '8px',
								padding: isSmallScreen ? '4px' : '8px',
								marginBottom: isSmallScreen ? '8px' : '16px',
								fontSize: isSmallScreen ? '0.75rem' : isMobile ? '0.875rem' : '1rem',
								flexWrap: isMobile ? 'wrap' : 'nowrap',
								gap: isMobile ? '4px' : '8px',
							},
						},
					}}
				/>
			</CalendarStyles>
		</StyledContainer>
	);
}

export default EventsCalendar;